import { call } from '@advanza/api'
import { Col, Row } from '@advanza/grid'
import { InputField, SelectSimple } from '@advanza/input'
import { Button, Divider, LoadingDots } from '@advanza/ui'
import clsx from 'clsx'
import DatePicker from 'components/DatePicker'
import { useToast } from 'components/misc/Toaster'
import { useCurrentCountry } from 'hooks/miscHooks'
import React, { useEffect, useState } from 'react'

interface Franchise {
    value: number
    title: string
    country_code: string
}

interface FranchiseProvider {
    service_provider_id: number
    business_name: string
    country_code: string
    status: number
    has_active_presence_subscription: boolean
    current_subscription: any
    eligible_for_presence: boolean
    highest_presence_price: number
    selected_service: any
    selected_subscription: any
}

interface FranchiseData {
    franchise: {
        service_provider_id: number
        business_name: string
        country_code: string
    }
    providers: FranchiseProvider[]
    total_providers: number
    providers_with_subscriptions: number
    providers_eligible: number
}

interface ActionResult {
    provider_id: number
    business_name: string
    action: string
    success: boolean
    message: string
    details: Record<string, any>
}

interface ActionSummary {
    total_providers: number
    successful_operations: number
    failed_operations: number
    success_rate: number
    action_breakdown: Record<string, number>
}

const FranchisePresenceSubscriptionsManager: React.FC = () => {
    const { addToast } = useToast()
    const currentCountry = useCurrentCountry()

    // State
    const [franchises, setFranchises] = useState<Franchise[]>([])
    const [selectedFranchiseId, setSelectedFranchiseId] = useState<number | null>(null)
    const [franchiseData, setFranchiseData] = useState<FranchiseData | null>(null)
    const [isLoadingFranchises, setIsLoadingFranchises] = useState(false)
    const [isLoadingProviders, setIsLoadingProviders] = useState(false)
    const [isExecutingAction, setIsExecutingAction] = useState(false)
    const [actionResults, setActionResults] = useState<ActionResult[]>([])
    const [actionSummary, setActionSummary] = useState<ActionSummary | null>(null)

    // Form state
    const [action, setAction] = useState<string>('enable_presence')
    const [overrideAmount, setOverrideAmount] = useState<string>('')
    const [startDate, setStartDate] = useState<string>('')
    const [discountType, setDiscountType] = useState<string>('percentage')
    const [discountAmount, setDiscountAmount] = useState<string>('')
    const [discountEndDate, setDiscountEndDate] = useState<string>('')

    // Load franchises on component mount and when country changes
    useEffect(() => {
        loadFranchises()
    }, [currentCountry])

    // Load providers when franchise selection changes
    useEffect(() => {
        if (selectedFranchiseId) {
            loadFranchiseProviders(selectedFranchiseId)
        } else {
            setFranchiseData(null)
            setActionResults([])
            setActionSummary(null)
        }
    }, [selectedFranchiseId])

    const loadFranchises = async () => {
        if (!currentCountry) return

        setIsLoadingFranchises(true)
        try {
            const response = await call(
                `office/franchise-presence-subscriptions/get-franchises?countryCode=${currentCountry}`,
            )
            setFranchises(response.franchises || [])
        } catch (error) {
            console.error('Failed to load franchises:', error)
            addToast({ msg: 'Error loading franchises', red: true })
        } finally {
            setIsLoadingFranchises(false)
        }
    }

    const loadFranchiseProviders = async (franchiseId: number) => {
        setIsLoadingProviders(true)
        try {
            const response = await call(
                `office/franchise-presence-subscriptions/get-franchise-providers/${franchiseId}`,
            )
            setFranchiseData(response)
            setActionResults([])
            setActionSummary(null)
        } catch (error) {
            console.error('Failed to load franchise providers:', error)
            addToast({ msg: 'Error loading franchise providers', red: true })
        } finally {
            setIsLoadingProviders(false)
        }
    }

    const executeAction = async () => {
        if (!selectedFranchiseId) {
            addToast({ msg: 'Please select a franchise first', red: true })
            return
        }

        setIsExecutingAction(true)
        try {
            const actionData: {
                action: string
                override_amount?: number
                start_date?: string
                discount_amount?: number
                discount_type?: string
                discount_end_date?: string
            } = { action }

            // Add action-specific data
            switch (action) {
                case 'enable_presence':
                    if (overrideAmount) actionData.override_amount = parseFloat(overrideAmount)
                    if (startDate) actionData.start_date = startDate
                    break
                case 'update_prices':
                    if (overrideAmount) actionData.override_amount = parseFloat(overrideAmount)
                    break
                case 'add_discounts':
                    if (discountAmount) actionData.discount_amount = parseFloat(discountAmount)
                    actionData.discount_type = discountType
                    if (discountEndDate) actionData.discount_end_date = discountEndDate
                    break
            }

            const response = await call(
                `office/franchise-presence-subscriptions/apply-to-franchise/${selectedFranchiseId}`,
                {
                    method: 'POST',
                    json: actionData,
                },
            )

            setActionResults(response.results || [])
            setActionSummary(response.summary || null)

            // Refresh franchise data
            await loadFranchiseProviders(selectedFranchiseId)

            addToast({ msg: `Action '${action}' completed successfully`, green: true })
        } catch (error) {
            console.error('Failed to execute action:', error)
            addToast({ msg: 'Error executing action', red: true })
        } finally {
            setIsExecutingAction(false)
        }
    }

    const resetForm = () => {
        setOverrideAmount('')
        setStartDate('')
        setDiscountType('percentage')
        setDiscountAmount('')
        setDiscountEndDate('')
    }

    const getActionDescription = (actionType: string): string => {
        switch (actionType) {
            case 'enable_presence':
                return 'Enable Presence Subscriptions'
            case 'update_prices':
                return 'Update Prices'
            case 'add_discounts':
                return 'Add Discounts'
            case 'disable_presence':
                return 'Disable Presence Subscriptions'
            default:
                return actionType
        }
    }

    const getProviderStatusBadge = (provider: FranchiseProvider) => {
        if (provider.has_active_presence_subscription) {
            return (
                <span className="px-2 py-1 text-sm font-medium bg-green-200 text-green-800 rounded-full">
                    Active
                </span>
            )
        }
        if (provider.eligible_for_presence) {
            return (
                <span className="px-2 py-1 text-sm font-medium bg-yellow-100 text-yellow-800 rounded-full">
                    Eligible
                </span>
            )
        }
        return (
            <span className="px-2 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full">
                Not Eligible
            </span>
        )
    }

    const getActionForm = () => {
        switch (action) {
            case 'enable_presence':
                return (
                    <>
                        <Col xs={12} md={6}>
                            <div>
                                <label className="block text-md font-bold text-neutral-700 mb-1">
                                    Override Amount (€)
                                </label>
                                <InputField
                                    type="number"
                                    step="0.01"
                                    value={overrideAmount}
                                    onChange={(e) => setOverrideAmount(e.target.value)}
                                    placeholder="Leave empty for default pricing"
                                />
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <DatePicker
                                name="start_date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                placeholder="Start Date"
                                legend
                            />
                        </Col>
                    </>
                )

            case 'update_prices':
                return (
                    <Col xs={12} md={6}>
                        <div>
                            <label className="block text-md font-bold text-neutral-700 mb-1">
                                Override Amount (€)
                            </label>
                            <InputField
                                type="number"
                                step="0.01"
                                value={overrideAmount}
                                onChange={(e) => setOverrideAmount(e.target.value)}
                                placeholder="Enter new override amount"
                            />
                        </div>
                    </Col>
                )

            case 'add_discounts':
                return (
                    <>
                        <Col xs={12} md={3}>
                            <div>
                                <label className="block text-md font-bold text-neutral-700 mb-1">
                                    Discount Type
                                </label>
                                <SelectSimple
                                    value={discountType}
                                    onChange={(e) => setDiscountType(String(e.target.value))}
                                    options={[
                                        { value: 'percentage', name: 'Percentage' },
                                        { value: 'amount', name: 'Fixed Amount' },
                                    ]}
                                />
                            </div>
                        </Col>
                        <Col xs={12} md={3}>
                            <div>
                                <label className="block text-md font-bold text-neutral-700 mb-1">
                                    {discountType === 'percentage'
                                        ? 'Discount %'
                                        : 'Discount Amount (€)'}
                                </label>
                                <InputField
                                    type="number"
                                    step={discountType === 'percentage' ? '1' : '0.01'}
                                    value={discountAmount}
                                    onChange={(e) => setDiscountAmount(e.target.value)}
                                    placeholder={discountType === 'percentage' ? '50' : '25.00'}
                                />
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div>
                                <label className="block text-md font-bold text-neutral-700 mb-1">
                                    End Date (Optional)
                                </label>
                                <InputField
                                    type="date"
                                    value={discountEndDate}
                                    onChange={(e) => setDiscountEndDate(e.target.value)}
                                />
                            </div>
                        </Col>
                    </>
                )

            default:
                return null
        }
    }

    return (
        <div className="franchise-presence-subscriptions-manager">
            <div className="mb-4">
                <p className="text-muted">
                    Manage presence subscriptions across all franchise child providers at once.
                    Select a franchise and apply settings to enable, update, or disable presence
                    subscriptions.
                </p>
            </div>

            {/* Franchise Selection */}
            <Row>
                <Col xs={12} md={6}>
                    <div>
                        <label className="block text-md font-bold text-neutral-700 mb-1">
                            Select Franchise
                        </label>
                        <SelectSimple
                            value={selectedFranchiseId || ''}
                            onChange={(e) => {
                                const value = String(e.target.value)
                                setSelectedFranchiseId(value ? parseInt(value) : null)
                            }}
                            options={[
                                { value: '', name: 'Choose a franchise...' },
                                ...franchises.map((franchise) => ({
                                    value: franchise.value,
                                    name: `${franchise.title} (${franchise.country_code})`,
                                })),
                            ]}
                            disabled={isLoadingFranchises}
                        />
                    </div>
                    {isLoadingFranchises && <span className="ml-2">Loading...</span>}
                </Col>
            </Row>

            {selectedFranchiseId && (
                <>
                    <Divider m />

                    {/* Franchise Summary */}
                    {isLoadingProviders ? (
                        <>
                            <LoadingDots />
                            <Divider m />
                        </>
                    ) : franchiseData ? (
                        <>
                            <Row>
                                <Col xs={12}>
                                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                                        <strong>{franchiseData.franchise.business_name}</strong> -
                                        {franchiseData.total_providers} total providers,
                                        {franchiseData.providers_with_subscriptions} with active
                                        subscriptions,
                                        {franchiseData.providers_eligible} eligible for presence
                                        subscriptions
                                    </div>
                                </Col>
                            </Row>
                            <Divider m />
                        </>
                    ) : null}

                    {/* Action Selection and Form */}
                    <Row>
                        <Col xs={12} md={4}>
                            <div>
                                <label className="block text-md font-bold text-neutral-700 mb-1">
                                    Action
                                </label>
                                <SelectSimple
                                    value={action}
                                    onChange={(e) => setAction(String(e.target.value))}
                                    options={[
                                        {
                                            value: 'enable_presence',
                                            name: 'Enable Presence Subscriptions',
                                        },
                                        { value: 'update_prices', name: 'Update Prices' },
                                        { value: 'add_discounts', name: 'Add Discounts' },
                                        {
                                            value: 'disable_presence',
                                            name: 'Disable Presence Subscriptions',
                                        },
                                    ]}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Divider m />
                    {/* Dynamic Action Form */}
                    {action !== 'disable_presence' && <Row>{getActionForm()}</Row>}
                    <Divider m />
                    {/* Action Buttons */}
                    <Row>
                        <Col xs={12}>
                            <Button
                                onClick={executeAction}
                                disabled={isExecutingAction || isLoadingProviders}
                                className="mr-2">
                                {isExecutingAction
                                    ? 'Executing...'
                                    : `Execute ${getActionDescription(action)}`}
                            </Button>
                            <Button
                                onClick={resetForm}
                                variant="outline"
                                disabled={isExecutingAction}>
                                Reset Form
                            </Button>
                        </Col>
                    </Row>

                    {/* Results */}
                    {actionResults.length > 0 && (
                        <>
                            <Divider m />
                            <Row>
                                <Col xs={12}>
                                    <h4>Action Results</h4>
                                    {actionSummary && (
                                        <div
                                            className={clsx(
                                                'p-4 border rounded-md',
                                                actionSummary.success_rate === 100
                                                    ? 'bg-green-50 border-green-200'
                                                    : 'bg-yellow-50 border-yellow-200',
                                            )}>
                                            <strong>Summary:</strong>{' '}
                                            {actionSummary.successful_operations}/
                                            {actionSummary.total_providers} operations successful (
                                            {actionSummary.success_rate}% success rate)
                                        </div>
                                    )}

                                    <div className="results-table">
                                        <table className="w-full">
                                            <thead>
                                                <tr>
                                                    <th className="text-left">Provider</th>
                                                    <th className="text-left">Action</th>
                                                    <th className="text-left">Status</th>
                                                    <th className="text-left">Message</th>
                                                    <th className="text-left">Details</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {actionResults.map((result, index) => (
                                                    <tr
                                                        key={index}
                                                        className={clsx(
                                                            result.success
                                                                ? 'bg-green-50'
                                                                : 'bg-red-50',
                                                        )}>
                                                        <td>{result.business_name}</td>
                                                        <td>
                                                            {getActionDescription(result.action)}
                                                        </td>
                                                        <td>
                                                            {result.success ? (
                                                                <span className="px-2 py-1 text-sm font-medium bg-green-200 text-green-800 rounded-full">
                                                                    Success
                                                                </span>
                                                            ) : (
                                                                <span className="px-2 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full">
                                                                    Failed
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>{result.message}</td>
                                                        <td>
                                                            {Object.entries(result.details).map(
                                                                ([key, value]) => (
                                                                    <div
                                                                        key={key}
                                                                        className="text-md">
                                                                        <strong>{key}:</strong>{' '}
                                                                        {String(value)}
                                                                    </div>
                                                                ),
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* Provider List */}
                    {franchiseData && (
                        <>
                            <Divider m />
                            <Row>
                                <Col xs={12}>
                                    <h4>Franchise Providers</h4>
                                    <div className="providers-table">
                                        <table className="w-full">
                                            <thead>
                                                <tr>
                                                    <th className="text-left">Provider ID</th>
                                                    <th className="text-left">Provider</th>
                                                    <th className="text-left">Status</th>
                                                    <th className="text-left">
                                                        Presence Subscription
                                                    </th>
                                                    <th className="text-left">Eligibility</th>
                                                    <th className="text-left">Highest Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {franchiseData.providers.map((provider) => (
                                                    <tr key={provider.service_provider_id}>
                                                        <td>
                                                            <a
                                                                href={`/service/providers/provider/${provider.service_provider_id}`}
                                                                className="text-primary-600 underline font-mono text-md"
                                                                target="_blank"
                                                                rel="noopener noreferrer">
                                                                {provider.service_provider_id}
                                                            </a>
                                                        </td>
                                                        <td>{provider.business_name}</td>
                                                        <td>{getProviderStatusBadge(provider)}</td>
                                                        <td>
                                                            {provider.has_active_presence_subscription ? (
                                                                <div>
                                                                    <div>Active</div>
                                                                    {provider.current_subscription
                                                                        ?.subscription_override_amount && (
                                                                        <div className="text-md text-muted">
                                                                            Override: €
                                                                            {
                                                                                provider
                                                                                    .current_subscription
                                                                                    .subscription_override_amount
                                                                            }
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">
                                                                    None
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {provider.eligible_for_presence ? (
                                                                <span className="px-2 py-1 text-sm font-medium bg-green-200 text-green-800 rounded-full">
                                                                    Eligible
                                                                </span>
                                                            ) : (
                                                                <span className="px-2 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full">
                                                                    Not Eligible
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {provider.highest_presence_price > 0 ? (
                                                                <span>
                                                                    €
                                                                    {
                                                                        provider.highest_presence_price
                                                                    }
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">
                                                                    N/A
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </>
            )}
        </div>
    )
}

export default FranchisePresenceSubscriptionsManager
